import { useEffect } from 'react';
import { usePosUiStore, type PosShortcutAction, type PosShortcutBindings } from '@/store/posUiStore';
import { matchPosShortcut } from '@/data/posShortcuts';

function isEditableTarget(el: EventTarget | null) {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable;
}

export interface PosShortcutHandlers {
  showShortcuts?: () => void;
  focusSearch?: () => void;
  focusBarcode?: () => void;
  toggleFavorites?: () => void;
  openPay?: () => void;
  checkout?: () => void;
  closeModal?: () => void;
  holdCart?: () => void;
  toggleHeldCarts?: () => void;
  openExpense?: () => void;
  openReturn?: () => void;
  openTodaySales?: () => void;
  openPending?: () => void;
  removeCartLine?: () => void;
  increaseCartQty?: () => void;
  decreaseCartQty?: () => void;
  prevCartLine?: () => void;
  nextCartLine?: () => void;
  cyclePaymentMode?: () => void;
  paymentModeCash?: () => void;
  paymentModeBank?: () => void;
  paymentModeBoth?: () => void;
  completeSale?: () => void;
  confirmPayment?: () => void;
}

const ACTION_MAP: Record<PosShortcutAction, keyof PosShortcutHandlers> = {
  showShortcuts: 'showShortcuts',
  focusSearch: 'focusSearch',
  focusBarcode: 'focusBarcode',
  toggleFavorites: 'toggleFavorites',
  openPay: 'openPay',
  checkout: 'checkout',
  closeModal: 'closeModal',
  holdCart: 'holdCart',
  toggleHeldCarts: 'toggleHeldCarts',
  openExpense: 'openExpense',
  openReturn: 'openReturn',
  openTodaySales: 'openTodaySales',
  openPending: 'openPending',
  removeCartLine: 'removeCartLine',
  increaseCartQty: 'increaseCartQty',
  decreaseCartQty: 'decreaseCartQty',
  prevCartLine: 'prevCartLine',
  nextCartLine: 'nextCartLine',
  cyclePaymentMode: 'cyclePaymentMode',
  paymentModeCash: 'paymentModeCash',
  paymentModeBank: 'paymentModeBank',
  paymentModeBoth: 'paymentModeBoth',
  completeSale: 'completeSale',
  confirmPayment: 'confirmPayment',
};

const GLOBAL_WHEN_TYPING: PosShortcutAction[] = [
  'showShortcuts',
  'openPay',
  'checkout',
  'closeModal',
  'holdCart',
  'toggleHeldCarts',
  'openExpense',
  'openReturn',
  'openTodaySales',
  'openPending',
  'completeSale',
  'confirmPayment',
  'toggleFavorites',
  'cyclePaymentMode',
  'paymentModeCash',
  'paymentModeBank',
  'paymentModeBoth',
];

export function usePosKeyboardShortcuts(
  handlers: PosShortcutHandlers,
  enabled = true,
  bindings?: PosShortcutBindings,
  /** When set, only these actions are listened for */
  onlyActions?: PosShortcutAction[],
) {
  const storeBindings = usePosUiStore((s) => s.shortcuts);
  const activeBindings = bindings || storeBindings;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const entries = Object.entries(activeBindings) as [PosShortcutAction, string][];
      const filtered = onlyActions
        ? entries.filter(([action]) => onlyActions.includes(action))
        : entries;

      if (isEditableTarget(event.target)) {
        for (const [action, binding] of filtered) {
          if (!GLOBAL_WHEN_TYPING.includes(action)) continue;
          if (matchPosShortcut(event, binding)) {
            event.preventDefault();
            event.stopPropagation();
            const handler = handlers[ACTION_MAP[action]];
            if (handler) handler();
            return;
          }
        }
        return;
      }

      for (const [action, binding] of filtered) {
        if (matchPosShortcut(event, binding)) {
          event.preventDefault();
          event.stopPropagation();
          const handler = handlers[ACTION_MAP[action]];
          if (handler) handler();
          return;
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [enabled, handlers, activeBindings, onlyActions]);
}
